import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Create } from '@refinedev/antd';
import { useList } from '@refinedev/core';
import { Form, Input, Select, Switch, Checkbox, Button, message, Alert } from 'antd';
import { authClient } from '../../providers/axios';
import { useRoleAccess, type RoleAccess } from './utils/roleAccess';
import { IdentityErrorAlert } from './components/IdentityErrorAlert';
import { PermissionPicker } from './components/PermissionPicker';
import { getRolePrefix, buildRoleName, validateCustomerRoleName } from './utils/roleNameUtils';

interface Permission {
  key: string;
  description?: string;
  category?: string;
  scope?: 'internal' | 'customer';
}

export const RolesCreatePage: React.FC = () => {
  const access = useRoleAccess();
  // Wait for the person's permissions, so the form starts on the right scope.
  if (!access.loaded) {
    return (
      <Create title="Create Role" saveButtonProps={{ style: { display: 'none' } }}>
        <div>Loading...</div>
      </Create>
    );
  }
  if (access.identityError) {
    return (
      <Create title="Create Role" saveButtonProps={{ style: { display: 'none' } }}>
        <IdentityErrorAlert access={access} />
      </Create>
    );
  }
  return <RolesCreateForm access={access} />;
};

const RolesCreateForm: React.FC<{ access: RoleAccess }> = ({ access }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  // A customer role manager can only create customer-scoped roles.
  const { customerOnly, limitedCompanies } = access;
  const initialScope = customerOnly ? 'customer' : 'internal';
  const [scope, setScope] = useState<'internal' | 'customer'>(initialScope);
  const [isShared, setIsShared] = useState(false);
  const [companyId, setCompanyId] = useState<string>('');

  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = useList<Permission>({
    resource: 'permissions',
    meta: { dataProviderName: 'authDataProvider' },
    pagination: { mode: 'off' },
  });

  const allPermissions = permissionsData?.data ?? [];

  // Filter permissions by scope, and to the ones this person may grant
  const filteredPermissions = allPermissions.filter((p) => {
    if (!access.canGrant(p.key)) return false;
    if (scope === 'customer') return p.scope === 'customer';
    return true; // internal scope shows all
  });

  const prefix = getRolePrefix(scope, companyId || null, isShared);

  const handleSubmit = async (values: {
    name: string;
    description: string;
    scope: string;
    companyId?: string;
    permissions: string[];
  }) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const fullName = buildRoleName(
        values.name,
        values.scope as 'internal' | 'customer',
        values.companyId || null,
        isShared,
      );
      await authClient.post('/roles', {
        ...values,
        name: fullName,
        companyId: values.scope === 'customer' && !isShared ? values.companyId || null : null,
      });
      message.success('Role created');
      navigate('/roles');
    } catch {
      message.error('Failed to create role');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Create title="Create Role" saveButtonProps={{ style: { display: 'none' } }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ scope: initialScope, permissions: [] }}
      >
        <Form.Item
          label="Scope"
          name="scope"
          rules={[{ required: true, message: 'Scope is required' }]}
        >
          <Select
            disabled={customerOnly}
            options={[
              ...(customerOnly ? [] : [{ label: 'Internal', value: 'internal' }]),
              { label: 'Customer', value: 'customer' },
            ]}
            onChange={(val) => {
              setScope(val);
              setIsShared(false);
              setCompanyId('');
              form.setFieldsValue({ companyId: '', permissions: [] });
            }}
          />
        </Form.Item>

        {/* A manager limited to some companies can't make roles shared by every customer. */}
        {scope === 'customer' && !limitedCompanies && (
          <Form.Item label="Shared Role (applies to all customers)">
            <Switch
              checked={isShared}
              onChange={(checked) => {
                setIsShared(checked);
                if (checked) {
                  setCompanyId('');
                  form.setFieldsValue({ companyId: '' });
                }
              }}
            />
          </Form.Item>
        )}

        {scope === 'customer' && !isShared && (
          <Form.Item
            label="Company ID"
            name="companyId"
            rules={[
              { required: true, message: 'Company ID is required for customer-scoped roles' },
            ]}
          >
            {limitedCompanies ? (
              <Select
                placeholder="Company ID"
                options={limitedCompanies.map((c) => ({ label: c, value: c }))}
                onChange={(val: string) => setCompanyId(val)}
              />
            ) : (
              <Input placeholder="Company ID" onChange={(e) => setCompanyId(e.target.value)} />
            )}
          </Form.Item>
        )}

        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: 'Name is required' },
            {
              validator: (_, value) => {
                if (scope === 'customer' && !isShared && value) {
                  const err = validateCustomerRoleName(value);
                  if (err) return Promise.reject(err);
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            addonBefore={prefix || undefined}
            placeholder={
              prefix ? 'Role name suffix' : 'Role name (must not start with sf_ or default_)'
            }
          />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'Description is required' }]}
        >
          <Input.TextArea rows={3} placeholder="Describe what this role is for" />
        </Form.Item>

        <Form.Item label="Permissions" name="permissions">
          {permissionsLoading ? (
            <div>Loading permissions...</div>
          ) : permissionsError ? (
            <Alert type="error" showIcon message="Couldn't load the permissions list" />
          ) : (
            <Checkbox.Group style={{ width: '100%' }}>
              <PermissionPicker permissions={filteredPermissions} />
            </Checkbox.Group>
          )}
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create Role
          </Button>
        </Form.Item>
      </Form>
    </Create>
  );
};
